import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState, BusinessOwner } from '../types';
import { api, MEDLEY_DEFAULT_VENUE_ID } from '../api/client';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ role: string; venueId: string | null }>;
  signup: (email: string, password: string, name: string, phone: string) => Promise<void>;
  logout: () => void;
  switchVenue: (venueId: string, venueName: string) => Promise<void>;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Check for stored session
    const storedUser = localStorage.getItem('okc_business_user');
    const storedToken = localStorage.getItem('okc_auth_token');

    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        if (user.role !== 'SUPERADMIN') {
          throw new Error('Medley requires a super administrator session');
        }
        // Tokens carry a venue context. Re-scope every restored superadmin
        // session to Medley so this single-tenant portal cannot accidentally
        // operate against the admin's original venue.
        api.setSelectedVenueId(MEDLEY_DEFAULT_VENUE_ID);
        api.switchVenue(MEDLEY_DEFAULT_VENUE_ID)
          .then(() => setState({ isAuthenticated: true, user, loading: false, error: null }))
          .catch(() => {
            localStorage.removeItem('okc_business_user');
            api.logout();
            setState((prev) => ({ ...prev, loading: false }));
          });
      } catch {
        localStorage.removeItem('okc_business_user');
        localStorage.removeItem('okc_auth_token');
        api.logout();
        setState((prev) => ({ ...prev, loading: false }));
      }
    } else {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ role: string; venueId: string | null }> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await api.login(email, password);
      if (response.admin.role !== 'SUPERADMIN') {
        api.logout();
        throw new Error('Medley is currently limited to super administrators');
      }

      // Map API response to BusinessOwner interface
      // Authentication is shared with the main admin portal, but Medley is a
      // single-tenant surface: every fresh login starts at its home venue.
      const selectedVenueId = MEDLEY_DEFAULT_VENUE_ID;
      await api.switchVenue(selectedVenueId);
      const user: BusinessOwner = {
        id: response.admin.id,
        email: response.admin.email,
        name: response.admin.email.split('@')[0], // Use email prefix as name for now
        phone: '',
        businessIds: [selectedVenueId],
        role: response.admin.role,
        subscriptionStatus: 'active',
        subscriptionPlan: response.admin.role === 'SUPERADMIN' ? 'enterprise' : 'pro',
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem('okc_business_user', JSON.stringify(user));

      api.setSelectedVenueId(selectedVenueId);

      setState({
        isAuthenticated: true,
        user,
        loading: false,
        error: null,
      });

      return { role: response.admin.role, venueId: selectedVenueId };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: message,
      });
      throw err;
    }
  };

  const signup = async (email: string, password: string, name: string, phone: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Signup not yet implemented in API - show helpful message
    setState({
      isAuthenticated: false,
      user: null,
      loading: false,
      error: 'Signup is not available yet. Please contact support to create an account.',
    });
    throw new Error('Signup is not available yet. Please contact support to create an account.');
  };

  const logout = () => {
    api.logout();
    localStorage.removeItem('okc_business_user');
    setState({
      isAuthenticated: false,
      user: null,
      loading: false,
      error: null,
    });
  };

  const switchVenue = async (venueId: string, venueName: string) => {
    try {
      // Set the selected venue ID in the API client
      api.setSelectedVenueId(venueId);

      // Update user with new venue
      if (state.user) {
        const updatedUser = {
          ...state.user,
          businessIds: [venueId],
        };
        localStorage.setItem('okc_business_user', JSON.stringify(updatedUser));
        setState(prev => ({ ...prev, user: updatedUser }));
      }

      // Reload the page to refresh all data with new venue context
      window.location.reload();
    } catch (err) {
      throw err;
    }
  };

  const isSuperAdmin = state.user?.role === 'SUPERADMIN';

  return (
    <AuthContext.Provider value={{ ...state, login, signup, logout, switchVenue, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
